import { FC, ReactNode, useCallback } from 'react';

import { Accept, useDropzone } from 'react-dropzone';

import { MAX_RAW_FILE_SIZE } from '@constants/generalConstants';
import { RejectedFile } from '@pages/home-page/types';
import { handleFileRejection } from '@pages/home-page/utils';

interface StyledDropzoneProps {
  accept?: Accept;
  children?: ReactNode;
  maxSize?: number;
  multiple?: boolean;
  onFilesReject?: (rejectedFiles: RejectedFile[]) => void;
  onFilesDrop?(files: File[]): void;
}

const StyledDropzone: FC<StyledDropzoneProps> = ({
  accept,
  children,
  maxSize = MAX_RAW_FILE_SIZE,
  multiple = true,
  onFilesDrop = () => null,
  onFilesReject,
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => onFilesDrop(acceptedFiles),
    [onFilesDrop],
  );

  const onDropRejected = useCallback(
    (fileRejections: RejectedFile[]) => {
      handleFileRejection(fileRejections, onFilesReject);
    },
    [onFilesReject],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    multiple,
    maxSize,
    onDrop,
    onDropRejected,
    noDragEventsBubbling: true,
  });

  const handleEvent = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      className={`relative w-full h-full border ${
        isDragActive ? 'border-gray-4 border-dashed' : 'border-transparent'
      }`}
      {...getRootProps()}
    >
      {isDragActive && (
        <div className="absolute inset-0 z-10 flex justify-center items-center">
          <div className="absolute inset-0 bg-gray-4 opacity-15 backdrop-blur-sm"></div>
          <p className="relative text-dark-1 text-xl font-semibold">
            Drop your file here to upload
          </p>
        </div>
      )}
      <div className="w-full h-fit">
        <div
          className={`pb-10 ${isDragActive ? 'blur-sm' : ''}`}
          onClick={handleEvent}
          onKeyDown={handleEvent}
          onDrop={handleEvent}
        >
          {children}
        </div>
        <input {...getInputProps()} />
      </div>
    </div>
  );
};

export default StyledDropzone;
